// @ts-check
/* ═══════════════════════ WEATHER (N2) ═══════════════════════
   Live vær per dag via /api/weather.js (Open-Meteo proxy).
   Cache per koordinat i localStorage i 1 time.
   ═══════════════════════════════════════════════════════════ */

import { fetchWithTimeout } from './utils.js';

// tripDay (0..17) → { lat, lng, loc } for hver reisedag.
// Sjø-dager bruker omtrentlige koordinater nær cruise-ruten.
export const DAY_COORDS = [
  { lat: 49.289, lng: -123.112, loc: 'Vancouver' },        // 0  Oslo → Vancouver
  { lat: 49.289, lng: -123.112, loc: 'Vancouver' },        // 1  Vancouver
  { lat: 49.289, lng: -123.112, loc: 'Vancouver' },        // 2  Embarkering
  { lat: 54.00,  lng: -131.00,  loc: 'Inside Passage' },   // 3  Sea day
  { lat: 58.129, lng: -135.458, loc: 'Icy Strait Point' }, // 4
  { lat: 59.458, lng: -135.314, loc: 'Skagway' },          // 5
  { lat: 58.301, lng: -134.419, loc: 'Juneau' },           // 6
  { lat: 55.341, lng: -131.647, loc: 'Ketchikan' },        // 7
  { lat: 52.00,  lng: -130.00,  loc: 'Inside Passage' },   // 8  Sea day
  { lat: 50.674, lng: -120.327, loc: 'Kamloops' },         // 9
  { lat: 52.873, lng: -118.081, loc: 'Jasper' },           // 10
  { lat: 52.873, lng: -118.081, loc: 'Jasper' },           // 11
  { lat: 51.178, lng: -115.570, loc: 'Banff' },            // 12
  { lat: 51.178, lng: -115.570, loc: 'Banff' },            // 13
  { lat: 51.045, lng: -114.072, loc: 'Calgary' },          // 14
  { lat: 51.045, lng: -114.072, loc: 'Calgary' },          // 15
  { lat: 51.045, lng: -114.072, loc: 'Calgary' },          // 16
  { lat: 64.146, lng: -21.942,  loc: 'Reykjavík' },        // 17
];

// Reisestart som ren kalenderdato (UTC-basert, tidssone-uavhengig).
// NB: ikke bruk `new Date('...T00:00:00+02:00').toISOString()` her — det gir
// datoen FØR i UTC og dermed off-by-one i dagsoppslag.
const TRIP_START_UTC = Date.UTC(2026, 6, 17);
const CACHE_TTL_MS = 60 * 60 * 1000;
const HOURLY_TTL_MS = 30 * 60 * 1000; // timesprognosen fornyes oftere

function cacheKey(lat, lng){
  return `wx:${lat.toFixed(3)},${lng.toFixed(3)}`;
}

function readCache(key, allowStale = false, ttl = CACHE_TTL_MS){
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    const age = Date.now() - ts;
    if (age > ttl && !allowStale) return null;
    return { data, ts, age, stale: age > ttl };
  } catch { return null; }
}

function writeCache(key, data){
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/** Henter rå daglig prognose (max 7 dager fram). Returnerer {data, stale, age}. */
async function fetchForecast(lat, lng){
  const key = cacheKey(lat, lng);
  const fresh = readCache(key);
  if (fresh) return fresh;
  try {
    const r = await fetchWithTimeout(`/api/weather?lat=${lat}&lng=${lng}`);
    if (!r.ok) throw new Error('weather ' + r.status);
    const json = await r.json();
    writeCache(key, json);
    return { data: json, ts: Date.now(), age: 0, stale: false };
  } catch {
    // Offline / feilet: server siste kjente prognose hvis vi har én, og merk den gammel.
    return readCache(key, true);
  }
}

// Open-Meteo weather codes → enkelt ikon
function codeIcon(code){
  if (code == null) return '';
  if (code === 0) return '☀';
  if (code <= 3)  return '⛅';
  if (code <= 48) return '🌫';
  if (code <= 67) return '🌧';
  if (code <= 77) return '❄';
  if (code <= 82) return '🌧';
  if (code <= 86) return '❄';
  if (code <= 99) return '⛈';
  return '';
}

function isoFor(tripDay){
  return new Date(TRIP_START_UTC + tripDay * 86400000).toISOString().slice(0, 10);
}

/**
 * Henter vær for én reisedag. Returnerer null hvis dagen er utenfor
 * Open-Meteos 7-dagers vindu (typisk: før reisen, eller > 7 dager fram).
 *
 * @returns {Promise<{tmax:number, tmin:number, precip:number, icon:string, loc:string} | null>}
 */
export async function getDayWeather(tripDay){
  const c = DAY_COORDS[tripDay];
  if (!c) return null;
  const target = isoFor(tripDay);
  const res = await fetchForecast(c.lat, c.lng);
  const data = res?.data;
  if (!data?.daily?.time) return null;
  const idx = data.daily.time.indexOf(target);
  if (idx < 0) return null;
  // Defensiv felt-tilgang: en trunkert/cachet respons kan ha `daily.time` men
  // mangle (eller ha kortere) temperatur-arrays. `undefined[idx]` ville kastet
  // og — siden kallerne i todayBrief.js ikke try/catch'er — utløst den globale
  // «Noe gikk galt»-toasten for et helt godartet vær-hikk.
  const dly = data.daily;
  const tmax = dly.temperature_2m_max?.[idx];
  const tmin = dly.temperature_2m_min?.[idx];
  if (tmax == null || tmin == null) return null;
  return {
    tmax:   Math.round(tmax),
    tmin:   Math.round(tmin),
    precip: Number(dly.precipitation_sum?.[idx] || 0),
    icon:   codeIcon(dly.weathercode?.[idx]),
    loc:    c.loc,
    stale:  !!res.stale,
    ageMs:  res.age || 0,
  };
}

/* ── Time-for-time-matrise for Vær-fanen ─────────────────────────────
   Rader = reisedag (dato + sted), kolonner = klokkeslett (lokal tid på
   stedet — Open-Meteo leverer hourly.time i stedets egen tidssone).   */

export const MATRIX_HOURS = [6, 9, 12, 15, 18, 21];

/** Henter timesprognose (16 dager) for én koordinat, cachet 30 min. */
async function fetchHourlyForecast(lat, lng, force = false){
  const key = `wxh:${lat.toFixed(3)},${lng.toFixed(3)}`;
  if (!force) {
    const fresh = readCache(key, false, HOURLY_TTL_MS);
    if (fresh) return fresh;
  }
  try {
    const r = await fetchWithTimeout(`/api/weather?lat=${lat}&lng=${lng}&hourly=1`);
    if (!r.ok) throw new Error('weather ' + r.status);
    const json = await r.json();
    writeCache(key, json);
    return { data: json, ts: Date.now(), age: 0, stale: false };
  } catch {
    return readCache(key, true, HOURLY_TTL_MS);
  }
}

/**
 * Bygger hele matrisen: én rad per reisedag med celler for MATRIX_HOURS.
 * Celler utenfor prognosevinduet (Open-Meteo: ~16 dager fram) er null —
 * de fylles inn av seg selv etter hvert som datoen nærmer seg.
 *
 * @param {boolean} [force] hopp over cache (manuell «Oppdater»-knapp)
 * @returns {Promise<{rows:Array<{tripDay:number,iso:string,loc:string,cells:Array<{temp:number,code:number}|null>}>, updatedAt:number, stale:boolean}>}
 */
export async function getTripMatrix(force = false){
  // Én fetch per unik koordinat (flere reisedager deler sted)
  const uniq = new Map();
  for (const c of DAY_COORDS) {
    const k = `${c.lat.toFixed(3)},${c.lng.toFixed(3)}`;
    if (!uniq.has(k)) uniq.set(k, c);
  }
  const results = new Map();
  let stale = false;
  await Promise.all([...uniq.entries()].map(async ([k, c]) => {
    const res = await fetchHourlyForecast(c.lat, c.lng, force);
    if (res) { results.set(k, res); if (res.stale) stale = true; }
  }));

  const rows = DAY_COORDS.map((c, tripDay) => {
    const iso = isoFor(tripDay);
    const data = results.get(`${c.lat.toFixed(3)},${c.lng.toFixed(3)}`)?.data;
    const times = data?.hourly?.time;
    const cells = MATRIX_HOURS.map(h => {
      if (!times) return null;
      const idx = times.indexOf(`${iso}T${String(h).padStart(2, '0')}:00`);
      if (idx < 0) return null;
      const temp = data.hourly.temperature_2m?.[idx];
      if (temp == null) return null;
      return { temp: Math.round(temp), code: data.hourly.weathercode?.[idx] };
    });
    return { tripDay, iso, loc: c.loc, cells };
  });

  return { rows, updatedAt: Date.now(), stale };
}

/** Komprimert badge-tekst: "⛅ 14° · 2 mm" eller "⛅ 14°". */
export function formatBadge(w){
  if (!w) return '';
  const rain = w.precip >= 0.5 ? ` · ${w.precip.toFixed(0)} mm` : '';
  return `${w.icon} ${w.tmax}°${rain}`;
}
